import { useCallback, useState } from 'react';
import { ObjectSchema } from 'yup';

interface UseValidationResult<T> {
  errors: YupErrors<T> | null;
  isValid: boolean;
  reset: () => void;
  validate: (data: T) => Promise<boolean>;
  validateSync: (data: T) => void;
}

export type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never;
}[keyof T];

export type YupErrors<T> = {
  [K in KeysOfType<T, string | number | boolean | null>]: string[];
};

export function useValidatorSchemes<T>(
  objectScheme: ObjectSchema<{}, T, {}, ''>,
): UseValidationResult<T> {
  const [errors, setErrors] = useState<YupErrors<T> | null>(null);

  const reset = useCallback(() => {
    setErrors(null);
  }, []);

  const getErrors = (error: any) => {
    const errorTransformed = error.inner.reduce((acc: any, err: any) => {
      acc[err.path as keyof YupErrors<T>] = [
        ...(acc[err.path as keyof YupErrors<T>] || []),
        err.message,
      ];
      return acc;
    }, {} as YupErrors<T>);
    return errorTransformed;
  };

  const validate = useCallback(
    async (data: T) => {
      try {
        await objectScheme.validate(data, { abortEarly: false });
        setErrors(null);
        return true;
      } catch (error: any) {
        setErrors(getErrors(error));
        return false;
      }
    },
    [objectScheme],
  );

  const validateSync = useCallback(
    (data: T) => {
      try {
        objectScheme.validateSync(data, { abortEarly: false });
        setErrors(null);
        return true;
      } catch (error: any) {
        setErrors(getErrors(error));
        return false;
      }
    },
    [objectScheme],
  );

  const isValid = errors === null;
  return { errors, isValid, reset, validate, validateSync };
}
